import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Leaf, AlertTriangle, Edit2, Save, X, Upload, Plus, Trash2, Settings, History } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useAdmin } from "../context/AdminContext";
import { useLang } from "../context/LanguageContext";
import BackupModal from "../components/BackupModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SANDWICH_LOGO = "/logos/sandwich-logo.png";

const T = {
  fr: {
    tabs: { Sandwiches: "Sandwiches", Desserts: "Desserts", Boissons: "Boissons" },
    editMode: "Mode édition actif",
    saving: "Sauvegarde...",
    backups: "Sauvegardes",
    infoBanner: "🍺 Une boisson au bar est obligatoire pour s'installer en terrasse",
    allergens: "Allergènes",
    noAllergens: "Aucun allergène majeur",
    tapAllergens: "Tap pour allergènes",
    tapBack: "Tap pour retourner",
    toasted: "🔥 Toasté",
    vegan: "Végan",
    vegetarian: "Végé",
    footer: "Prix affichés en € • Service compris • CB à partir de 10€",
    addProduct: "Ajouter",
    addCategory: "Ajouter une catégorie",
    editProduct: "Modifier le produit",
    cancel: "Annuler",
    save: "Sauvegarder",
    vegLabel: "Végétarien",
    veganLabel: "Végan",
    toastedLabel: "Toasté",
    changeImage: "Changer l'image",
    namePlaceholder: "Nom",
    pricePlaceholder: "Prix (ex: 7,50)",
    descPlaceholder: "Description",
    allergensPlaceholder: "Allergènes (séparés par virgule)",
    sectionTitle: { Sandwiches: "Sandwiches & Salades", Desserts: "Desserts", Boissons: "Boissons" },
  },
  en: {
    tabs: { Sandwiches: "Sandwiches", Desserts: "Desserts", Boissons: "Drinks" },
    editMode: "Edit mode active",
    saving: "Saving...",
    backups: "Backups",
    infoBanner: "🍺 A drink from the bar is required to sit on the terrace",
    allergens: "Allergens",
    noAllergens: "No major allergens",
    tapAllergens: "Tap for allergens",
    tapBack: "Tap to go back",
    toasted: "🔥 Toasted",
    vegan: "Vegan",
    vegetarian: "Veggie",
    footer: "Prices in € • Service included • Card from €10",
    addProduct: "Add",
    addCategory: "Add a category",
    editProduct: "Edit product",
    cancel: "Cancel",
    save: "Save",
    vegLabel: "Vegetarian",
    veganLabel: "Vegan",
    toastedLabel: "Toasted",
    changeImage: "Change image",
    namePlaceholder: "Name",
    pricePlaceholder: "Price (e.g. 7.50)",
    descPlaceholder: "Description",
    allergensPlaceholder: "Allergens (comma-separated)",
    sectionTitle: { Sandwiches: "Sandwiches & Salads", Desserts: "Desserts", Boissons: "Drinks" },
  },
};

const TABS = [
  { id: "Sandwiches" },
  { id: "Desserts" },
  { id: "Boissons" },
];

// Edit Modal for product
function EditModal({ item, onSave, onClose, onUpload, t }) {
  const [formData, setFormData] = useState({ ...item });
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const url = await onUpload(file);
    if (url) setFormData({ ...formData, image: url });
    setUploading(false);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-end justify-center" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-t-3xl p-5 max-h-[85vh] overflow-y-auto animate-slide-up" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold font-['Oswald']">{t.editProduct}</h3>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="w-5 h-5" /></Button>
        </div>

        <div className="mb-4">
          <div className="relative h-32 bg-neutral-100 rounded-xl overflow-hidden cursor-pointer group" onClick={() => fileInputRef.current?.click()}>
            {formData.image ? (
              <img src={formData.image} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="flex items-center justify-center h-full text-neutral-400"><Upload className="w-8 h-8" /></div>
            )}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
              <span className="text-white text-sm">{uploading ? "Upload..." : t.changeImage}</span>
            </div>
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder={t.namePlaceholder} value={formData.name || ""} onChange={e => setFormData({ ...formData, name: e.target.value })} />
            <Input placeholder={t.pricePlaceholder} value={formData.price || ""} onChange={e => setFormData({ ...formData, price: e.target.value })} />
          </div>
          <Textarea placeholder={t.descPlaceholder} value={formData.description || ""} onChange={e => setFormData({ ...formData, description: e.target.value })} rows={2} />
          <Input placeholder={t.allergensPlaceholder} value={(formData.allergens || []).join(", ")} onChange={e => setFormData({ ...formData, allergens: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} />
          <div className="flex flex-wrap gap-4 py-2">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={formData.vegetarian || false} onChange={e => setFormData({ ...formData, vegetarian: e.target.checked })} className="rounded" />{t.vegLabel}</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={formData.vegan || false} onChange={e => setFormData({ ...formData, vegan: e.target.checked })} className="rounded" />{t.veganLabel}</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={formData.toasted || false} onChange={e => setFormData({ ...formData, toasted: e.target.checked })} className="rounded" />{t.toastedLabel}</label>
          </div>
        </div>

        <div className="flex gap-3 mt-5">
          <Button variant="outline" className="flex-1" onClick={onClose}>{t.cancel}</Button>
          <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => onSave(formData)}>
            <Save className="w-4 h-4 mr-2" />{t.save}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Product Card with edit capability
function ProductCard({ item, hideVeganBadges, isAdmin, onEdit, onDelete, t }) {
  const [isFlipped, setIsFlipped] = useState(false);

  return (
    <div className="perspective-1000 h-[220px] relative">
      <div 
        className={`relative w-full h-full transition-transform duration-500 transform-style-3d cursor-pointer ${isFlipped ? 'rotate-y-180' : ''}`}
        onClick={() => !isAdmin && setIsFlipped(!isFlipped)}
      >
        {/* Front */}
        <div className="absolute inset-0 backface-hidden bg-white rounded-xl shadow-sm border border-neutral-100 overflow-hidden">
          <div className="relative h-28 overflow-hidden">
            <img src={item.image} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
            
            {/* Admin buttons inside image */}
            {isAdmin && (
              <div className="absolute top-2 left-2 flex gap-1">
                <button 
                  onClick={(e) => { e.stopPropagation(); onEdit(); }}
                  className="w-8 h-8 bg-amber-500 text-white rounded-lg flex items-center justify-center shadow-lg hover:bg-amber-600"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button 
                  onClick={(e) => { e.stopPropagation(); onDelete(); }}
                  className="w-8 h-8 bg-red-500 text-white rounded-lg flex items-center justify-center shadow-lg hover:bg-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}
            
            {!hideVeganBadges && (
              <div className="absolute top-1.5 right-1.5 flex flex-col gap-1">
                {item.toasted && (
                  <Badge className="bg-amber-500 text-white hover:bg-amber-500 text-[10px] px-1.5 py-0.5">
                    {t.toasted}
                  </Badge>
                )}
                {item.vegan && (
                  <Badge className="bg-emerald-600 text-white hover:bg-emerald-600 text-[10px] px-1.5 py-0.5">
                    <Leaf className="w-2.5 h-2.5 mr-0.5" />{t.vegan}
                  </Badge>
                )}
                {item.vegetarian && !item.vegan && (
                  <Badge className="bg-green-500 text-white hover:bg-green-500 text-[10px] px-1.5 py-0.5">
                    <Leaf className="w-2.5 h-2.5 mr-0.5" />{t.vegetarian}
                  </Badge>
                )}
              </div>
            )}
            {!isAdmin && (
              <div className="absolute bottom-1.5 left-1.5 bg-black/50 text-white text-[9px] px-1.5 py-0.5 rounded-full">
                {t.tapAllergens}
              </div>
            )}
          </div>
          <div className="p-2.5">
            <div className="flex items-start justify-between gap-1">
              <h3 className="font-bold text-neutral-900 font-['Oswald'] text-sm leading-tight">{item.name}</h3>
              <span className="font-bold text-green-700 text-sm whitespace-nowrap">{item.price}€</span>
            </div>
            <p className="text-xs text-neutral-500 mt-1 leading-snug line-clamp-2">{item.description}</p>
          </div>
        </div>

        {/* Back - Allergens */}
        <div className="absolute inset-0 backface-hidden rotate-y-180 bg-white rounded-xl shadow-sm border border-neutral-100 overflow-hidden">
          <div className="h-full flex flex-col p-4">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-neutral-100">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <h4 className="font-bold text-sm text-neutral-900 font-['Oswald']">{t.allergens}</h4>
            </div>
            <div className="flex-1">
              {item.allergens?.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {item.allergens.map((a, i) => (
                    <span key={i} className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">{a}</span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-neutral-500 italic">{t.noAllergens}</p>
              )}
            </div>
            <p className="text-[10px] text-neutral-400 text-center pt-3 border-t">{t.tapBack}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function MenuSection({ id, title, items, hideVeganBadges, sectionRef, isAdmin, onEditItem, onDeleteItem, onAddItem, onDeleteCategory, onRenameCategory, t }) {
  const handleRename = () => {
    const newName = prompt("Nouveau nom de la catégorie :", title);
    if (newName) onRenameCategory(newName);
  };

  return (
    <section ref={sectionRef} id={id} className="scroll-mt-32 mb-8">
      <div className="flex items-center justify-between mb-3 px-1">
        <h2 className="text-lg font-bold uppercase tracking-wide text-green-800 font-['Oswald']">{title}</h2>
        {isAdmin && (
          <div className="flex gap-1">
            <button onClick={handleRename} className="p-1.5 text-green-600 hover:bg-green-100 rounded"><Edit2 className="w-4 h-4" /></button>
            <button onClick={onDeleteCategory} className="p-1.5 text-red-500 hover:bg-red-100 rounded"><Trash2 className="w-4 h-4" /></button>
            <Button size="sm" variant="outline" onClick={onAddItem} className="h-8 ml-2">
              <Plus className="w-4 h-4 mr-1" />{t.addProduct}
            </Button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        {items.map((item, idx) => (
          <ProductCard key={idx} item={item} hideVeganBadges={hideVeganBadges} isAdmin={isAdmin}
            onEdit={() => onEditItem(idx)} onDelete={() => onDeleteItem(idx)} t={t} />
        ))}
      </div>
    </section>
  );
}

export default function SandwichMenuPage() {
  const navigate = useNavigate();
  const { isAdmin, password, logout } = useAdmin();
  const { lang, toggleLang } = useLang();
  const t = T[lang];
  const [activeTab, setActiveTab] = useState("Sandwiches");
  const [menuData, setMenuData] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showBackups, setShowBackups] = useState(false);
  
  const sandwichesRef = useRef(null);
  const dessertsRef = useRef(null);
  const boissonsRef = useRef(null);

  const sectionRefs = { Sandwiches: sandwichesRef, Desserts: dessertsRef, Boissons: boissonsRef };

  // Load menu from API
  const loadMenu = () => {
    fetch(`${BACKEND_URL}/api/menu/sandwich`)
      .then(res => res.json())
      .then(data => setMenuData(data.data))
      .catch(err => console.error(err));
  };

  useEffect(() => {
    loadMenu();
  }, []);

  // Scroll spy
  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        { id: "Sandwiches", ref: sandwichesRef },
        { id: "Desserts", ref: dessertsRef },
        { id: "Boissons", ref: boissonsRef },
      ];
      for (let i = sections.length - 1; i >= 0; i--) {
        if (sections[i].ref.current?.getBoundingClientRect().top <= 150) {
          setActiveTab(sections[i].id);
          break;
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (tabId) => {
    setActiveTab(tabId);
    sectionRefs[tabId].current?.scrollIntoView({ behavior: "smooth" });
  };

  // Save menu to API
  const saveMenu = async (newData) => {
    setSaving(true);
    try {
      await fetch(`${BACKEND_URL}/api/admin/menu/sandwich`, {
        method: "PUT",
        headers: {
          "Authorization": "Basic " + btoa("admin:" + password),
          "Content-Type": "application/json"
        },
        body: JSON.stringify(newData)
      });
      setMenuData(newData);
    } catch (err) {
      alert("Erreur de sauvegarde");
    }
    setSaving(false);
  };

  // Upload image
  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/upload`, {
        method: "POST",
        headers: { "Authorization": "Basic " + btoa("admin:" + password) },
        body: formData
      });
      const data = await res.json();
      return BACKEND_URL + data.url;
    } catch (err) {
      alert("Erreur d'upload");
      return null;
    }
  };

  // Edit handlers
  const handleEditItem = (category, index) => {
    setEditingItem({ category, index, item: { ...menuData[category][index] } });
  };

  const handleSaveItem = (updatedItem) => {
    const { category, index } = editingItem;
    const newData = { ...menuData };
    newData[category][index] = updatedItem;
    saveMenu(newData);
    setEditingItem(null);
  };

  const handleDeleteItem = (category, index) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    const newData = { ...menuData };
    newData[category].splice(index, 1);
    saveMenu(newData);
  };

  const handleAddItem = (category) => {
    const newItem = {
      name: "Nouveau produit",
      description: "Description à modifier",
      price: "0,00",
      image: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&h=300&fit=crop&sat=-100&blur=2",
      vegetarian: false,
      vegan: false,
      toasted: false,
      allergens: []
    };
    const newData = { ...menuData };
    newData[category].push(newItem);
    saveMenu(newData);
  };

  const handleAddCategory = () => {
    const categoryName = prompt("Nom de la nouvelle catégorie :");
    if (!categoryName || categoryName.trim() === "") return;
    
    const newData = { ...menuData };
    newData[categoryName.trim()] = [];
    saveMenu(newData);
  };

  const handleDeleteCategory = (category) => {
    if (!window.confirm(`Supprimer la catégorie "${category}" et tous ses produits ?`)) return;
    const newData = { ...menuData };
    delete newData[category];
    saveMenu(newData);
  };

  const handleRenameCategory = (oldName, newName) => {
    if (!newName || newName.trim() === "" || newName === oldName) return;
    const newData = {};
    Object.entries(menuData).forEach(([key, value]) => {
      if (key === oldName) {
        newData[newName.trim()] = value;
      } else {
        newData[key] = value;
      }
    });
    saveMenu(newData);
  };

  if (!menuData) return (
    <div className="min-h-screen bg-neutral-100">
      <div className="sticky top-0 z-50 bg-[#166534] h-16 shadow-md" />
      <div className="max-w-md mx-auto px-3 py-4 space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} style={{ animationDelay: `${i * 80}ms` }} className="animate-fade-in-up">
            <div className="skeleton h-5 w-40 mb-3" />
            <div className="grid grid-cols-2 gap-3">
              {[...Array(4)].map((_, j) => (
                <div key={j} className="bg-white rounded-2xl overflow-hidden shadow-sm">
                  <div className="skeleton h-28 w-full rounded-none" />
                  <div className="p-3 space-y-2">
                    <div className="skeleton h-3.5 w-3/4" />
                    <div className="skeleton h-2.5 w-full" />
                    <div className="skeleton h-4 w-12 mt-1" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const sandwichItems = [...(menuData["Sandwiches"] || []), ...(menuData["Salades"] || [])];
  const dessertItems = menuData["Desserts"] || [];
  const boissonItems = menuData["Boissons"] || [];

  return (
    <div className="min-h-screen bg-neutral-100">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#166534] shadow-md">
        <div className="flex items-center justify-between px-4 py-2 max-w-md mx-auto">
          <Button variant="ghost" size="icon" onClick={() => navigate("/")} className="bg-white/90 hover:bg-white rounded-full shadow-sm h-9 w-9">
            <ArrowLeft className="w-5 h-5 text-green-800" />
          </Button>
          <img src={SANDWICH_LOGO} alt="Primo" className="h-12 w-auto rounded-xl" />
          <div className="flex items-center gap-2">
            <button
              onClick={toggleLang}
              className="text-xl leading-none rounded-full shadow-sm hover:opacity-80 transition-opacity"
              title={lang === "fr" ? "Switch to English" : "Passer en français"}
            >
              {lang === "fr" ? "🇬🇧" : "🇫🇷"}
            </button>
            {isAdmin ? (
              <Button variant="ghost" size="icon" onClick={logout} className="bg-amber-500 hover:bg-amber-600 rounded-full shadow-sm h-9 w-9" title="Déconnexion admin">
                <Settings className="w-5 h-5 text-white" />
              </Button>
            ) : (
              <div className="w-9" />
            )}
          </div>
        </div>
        
        {/* Admin banner */}
        {isAdmin && (
          <div className="bg-amber-500 text-white text-xs text-center py-1 font-medium flex items-center justify-center gap-4">
            <span className="flex items-center gap-1.5">
              {t.editMode}
              {saving && (
                <>
                  <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin-fast inline-block" />
                  {t.saving}
                </>
              )}
            </span>
            <button
              onClick={() => setShowBackups(true)}
              className="flex items-center gap-1 bg-white/20 hover:bg-white/30 px-2 py-0.5 rounded transition-colors"
            >
              <History className="w-3 h-3" />
              {t.backups}
            </button>
          </div>
        )}
        
        {/* Tabs */}
        <div className="bg-white border-t border-green-700">
          <div className="max-w-md mx-auto flex">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => scrollToSection(tab.id)}
                className={`flex-1 py-3 text-sm font-semibold uppercase tracking-wide transition-all ${
                  activeTab === tab.id
                    ? "text-green-700 border-b-2 border-green-600 bg-green-50"
                    : "text-neutral-500 hover:text-neutral-700"
                }`}
              >
                {t.tabs[tab.id]}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Info Banner */}
      <div className="bg-amber-50 border-b border-amber-200">
        <div className="max-w-md mx-auto px-4 py-2">
          <p className="text-xs text-amber-800 text-center font-medium">{t.infoBanner}</p>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-md mx-auto px-3 py-4 pb-8">
        <MenuSection
          id="sandwiches" title={t.sectionTitle.Sandwiches} items={sandwichItems}
          hideVeganBadges={false} sectionRef={sandwichesRef} isAdmin={isAdmin} t={t}
          onEditItem={(idx) => handleEditItem(idx < (menuData["Sandwiches"]?.length || 0) ? "Sandwiches" : "Salades", idx < (menuData["Sandwiches"]?.length || 0) ? idx : idx - (menuData["Sandwiches"]?.length || 0))}
          onDeleteItem={(idx) => handleDeleteItem(idx < (menuData["Sandwiches"]?.length || 0) ? "Sandwiches" : "Salades", idx < (menuData["Sandwiches"]?.length || 0) ? idx : idx - (menuData["Sandwiches"]?.length || 0))}
          onAddItem={() => handleAddItem("Sandwiches")}
          onDeleteCategory={() => {}} onRenameCategory={() => {}}
        />
        <MenuSection
          id="desserts" title={t.sectionTitle.Desserts} items={dessertItems}
          hideVeganBadges={false} sectionRef={dessertsRef} isAdmin={isAdmin} t={t}
          onEditItem={(idx) => handleEditItem("Desserts", idx)}
          onDeleteItem={(idx) => handleDeleteItem("Desserts", idx)}
          onAddItem={() => handleAddItem("Desserts")}
          onDeleteCategory={() => handleDeleteCategory("Desserts")}
          onRenameCategory={(newName) => handleRenameCategory("Desserts", newName)}
        />
        <MenuSection
          id="boissons" title={t.sectionTitle.Boissons} items={boissonItems}
          hideVeganBadges={true} sectionRef={boissonsRef} isAdmin={isAdmin} t={t}
          onEditItem={(idx) => handleEditItem("Boissons", idx)}
          onDeleteItem={(idx) => handleDeleteItem("Boissons", idx)}
          onAddItem={() => handleAddItem("Boissons")}
          onDeleteCategory={() => handleDeleteCategory("Boissons")}
          onRenameCategory={(newName) => handleRenameCategory("Boissons", newName)}
        />

        {/* Add category button */}
        {isAdmin && (
          <button
            onClick={handleAddCategory}
            className="w-full py-4 border-2 border-dashed border-green-300 rounded-xl text-green-600 font-medium hover:bg-green-50 transition-colors flex items-center justify-center gap-2 mt-4"
          >
            <Plus className="w-5 h-5" />
            {t.addCategory}
          </button>
        )}
      </main>

      {/* Edit Modal */}
      {editingItem && (
        <EditModal
          item={editingItem.item}
          onSave={handleSaveItem}
          onClose={() => setEditingItem(null)}
          onUpload={uploadImage}
          t={t}
        />
      )}

      {/* Backup Modal */}
      {showBackups && (
        <BackupModal
          menuType="sandwich"
          password={password}
          onClose={() => setShowBackups(false)}
          onRestore={loadMenu}
        />
      )}
    </div>
  );
}
